<script setup lang="ts">
import { computed } from 'vue';
import HeroCard from './HeroCard.vue';

interface HeroCardData {
  title: string;
  image: string;
  href: string;
}

interface Props {
  heading?: string;
  subheading?: string;
  heroImage?: string;
  heroImageAlt?: string;
  heroImageColFlex?: string;
  heroImageSize?: string;
  heroImagePosition?: string;
  heroImageRepeat?: string;
  cards?: HeroCardData[];
  bgColor?: string;
  /** Plain CTA label fallback (e.g. Hyper `text` or home default). */
  ctaLabel?: string;
  /** BaseButton `heading` HTML when hero CTA is Craft-driven. */
  ctaHeadingHtml?: string;
  ctaHref?: string;
  ctaTarget?: string;
  /**
   * `home` — larger heading (60–70px desktop), vertically centered as today.
   * `inner` — About / How it Works: 52px desktop heading, content nudged slightly lower.
   * `marketing` — wide right image column (65%), background anchored for landing-style pages.
   * `campaign` — deprecated alias for `marketing` (layout only; not related to section handle).
   */
  variant?: 'home' | 'inner' | 'marketing' | 'campaign';
}

const props = withDefaults(defineProps<Props>(), {
  heading: 'Put your best you forward.',
  subheading: 'Individualized treatment plans that meet you where you are.',
  heroImage: '',
  heroImageAlt: 'JRNYS',
  heroImageColFlex: '',
  heroImageSize: '',
  heroImagePosition: '',
  heroImageRepeat: '',
  bgColor: '#285254',
  cards: () => [],
  ctaLabel: '',
  ctaHeadingHtml: '',
  ctaHref: '',
  ctaTarget: '_self',
  variant: 'inner',
});

/** Normalized layout variant (`campaign` was renamed to `marketing`). */
const layoutVariant = computed(() => (props.variant === 'campaign' ? 'marketing' : props.variant));

const isHomeHero = computed(() => layoutVariant.value === 'home');

const ctaRichLabelVisible = computed(() => {
  const raw = props.ctaHeadingHtml ?? '';
  return raw.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
});

const showCta = computed(() => {
  const href = props.ctaHref?.trim() ?? '';
  if (!href) return false;
  if (ctaRichLabelVisible.value) return true;
  return Boolean(props.ctaLabel?.trim());
});

const headingClass = computed(() => {
  if (isHomeHero.value) {
    return 'font-inter-tight text-40 font-semibold leading-110 text-white sm:text-42 lg:max-w-[450px] lg:text-left lg:text-[60px] text-center xl:text-[70px]';
  }
  return 'font-inter-tight text-[36px] font-medium leading-110 text-white sm:text-42 lg:max-w-[560px] lg:font-semibold lg:text-[52px] lg:text-left text-center';
});

/** Marketing pages (inner + `marketing` variant): bottom-aligned content; home vertically centered. */
const contentColumnClass = computed(() => {
  if (isHomeHero.value) {
    return [
      'relative flex min-w-0 flex-1 flex-col justify-end pt-180 pb-8 lg:justify-center lg:pl-[120px] lg:pt-180 lg:pb-80 mt-[150px] lg:mt-48',
    ];
  }
  return [
    'relative flex min-w-0 flex-1 flex-col justify-end pb-8 pt-180 lg:justify-end lg:pb-48 lg:pt-180 mt-[150px] lg:mt-0',
  ];
});

const heroSectionStyle = computed(() => {
  const style: Record<string, string> = {
    backgroundColor: props.bgColor,
  };
  if (props.heroImage) {
    style.backgroundImage = `url(${props.heroImage})`;
    style.backgroundRepeat = 'no-repeat';
    if (isHomeHero.value) {
      /*
       * Mobile/tablet (<lg): section background only — position is set in scoped SCSS
       * (`down('lg')`) so it can differ from desktop <img> transforms.
       */
      style.backgroundRepeat = 'no-repeat';
    } else if (layoutVariant.value === 'marketing') {
      style.backgroundSize = 'cover';
      style.backgroundPosition = '30% top';
    } else {
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'top center';
    }
  }
  return style;
});

/** Marketing: wide column. Home: slightly narrower band than inner so subject reads closer to Figma’s right edge. */
const heroImgColumnClass = computed(() => {
  if (layoutVariant.value === 'marketing') return 'w-[65%]';
  if (layoutVariant.value === 'home') return 'w-[74%]';
  return 'w-3/4';
});

const heroBgImgWrapperClass = computed(() => [
  'site-hero__bg-img absolute inset-y-0 right-0 hidden overflow-hidden rounded-bl-[40px] lg:block',
  /* Home: keep photo under copy/cards so CTAs are not covered; inner/marketing keep prior stacking. */
  isHomeHero.value ? 'z-0' : 'z-[1]',
  heroImgColumnClass.value,
]);

</script>

<template>
  <section
    class="site-hero relative overflow-hidden"
    :class="{ 'site-hero--home': isHomeHero, 'site-hero--inner': !isHomeHero }"
    :style="heroSectionStyle"
  >
    <!-- Mobile/tablet: same bottom read overlay as inner/marketing heroes (hidden ≥lg). -->
    <div class="site-hero__gradient absolute inset-0 z-[1] lg:hidden" />
    <img
      v-if="heroImage"
      :src="heroImage"
      :alt="heroImageAlt"
      class="sr-only"
      fetchpriority="high"
    >

    <!-- Desktop background image (absolutely positioned right side) -->
    <div
      v-if="heroImage"
      :class="heroBgImgWrapperClass"
    >
      <img
        :src="heroImage"
        :alt="heroImageAlt"
        class="site-hero__photo size-full object-cover"
      >
    </div>

    <!-- Content flows naturally over the full container width -->
    <div
      class="container relative z-10 mb-24 flex min-h-[inherit] flex-col lg:mb-0"
      :class="[
        isHomeHero ? 'lg:min-h-[800px]' : 'lg:min-h-[600px]',
        isHomeHero ? 'lg:isolate' : '',
      ]"
    >
      <div :class="contentColumnClass">
        <div :class="isHomeHero ? 'lg:max-w-[550px]' : 'lg:max-w-[600px]'">
          <h1
            :class="headingClass"
            v-html="heading"
          />
          <div
            v-if="subheading"
            class="hero-subheading mx-16 mt-16 text-center font-inter text-16 leading-125 text-white lg:mx-0 lg:text-left lg:text-18 [&_a]:underline"
            v-html="subheading"
          />

          <a
            v-if="showCta"
            :href="ctaHref"
            :target="ctaTarget || '_self'"
            class="text-off-primary-500 mx-auto mt-24 flex w-fit max-w-full items-center justify-center rounded-full bg-white px-48 py-14 font-inter-tight text-14 font-medium tracking-wide transition-opacity hover:opacity-90 lg:mx-0 lg:px-40 lg:py-[15px] lg:text-16"
          >
            <span
              v-if="ctaRichLabelVisible"
              class="[&_p]:m-0"
              v-html="ctaHeadingHtml"
            />
            <template v-else>{{ ctaLabel }}</template>
          </a>
        </div>

        <!-- Cards -->
        <div
          v-if="cards?.length"
          class="mx-16 mt-32 flex max-w-[650px] flex-col gap-12 sm:flex-row lg:mx-0 lg:gap-24"
          :class="[
            isHomeHero ? 'lg:mt-72' : 'lg:mt-60',
            { 'lg:relative lg:z-20': isHomeHero },
          ]"
        >
          <HeroCard
            v-for="card in cards"
            :key="card.href"
            :title="card.title"
            :image="card.image"
            :href="card.href"
            class="flex-1"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.site-hero {
  // Pull hero up behind the header to fill .main-content padding
  margin-top: -100px;
  min-height: 600px;

  @include up('md') {
    margin-top: -150px;
  }

  @include up('lg') {
    min-height: 600px;
    background-image: none !important;
  }

  &--home {
    @include up('md') {
      @include down('lg') {
        min-height: 800px;
        background-size: 220% auto !important;
        background-position: 52% 18% !important;
      }
    }

    /* Mobile/tablet: full-bleed section bg — separate from desktop <img> framing (translate/scale). */
    @include down('lg') {
      /* Larger than `cover` so the portrait reads bigger; position stays at 40% 40%. */
      background-size: 300% auto !important;
      background-position: 40% 40% !important;
    }

    @include up('lg') {
      min-height: 800px;
    }

    :deep(.site-hero__bg-img .site-hero__photo) {
      @include down('lg') {
        transform: none !important;
        object-position: center 24% !important;
      }

      @include up('lg') {
        object-position: 100% 58%;
        transform: translate(60%, 25%) scale(1.8);
        transform-origin: 100% 52%;
      }
    }
  }

  &--inner {
    min-height: 680px;

    @include up('lg') {
      min-height: 600px;
    }
  }

  &__gradient {
    background: linear-gradient(
      to bottom,
      transparent 20%,
      rgba(9, 40, 42, 0.4) 50%,
      rgba(9, 40, 42, 0.4) 85%
    );
  }

  &__subheading {
    font-weight: 450;
    letter-spacing: -0.54px;
  }

  // CKEditor / rich text injects <p>, <strong>, etc. — keep typography sane under scoped styles
  h1 :deep(p) {
    margin: 0;
  }
  h1 :deep(p + p) {
    margin-top: 0.35em;
  }

  .hero-subheading :deep(p) {
    margin: 0;
  }
  .hero-subheading :deep(p + p) {
    margin-top: 0.75em;
  }
}
</style>
